import { z } from 'zod';

const status = z.enum(['none', 'playing', 'won', 'lost']);
const reward = {
  // Snapshot/answer payloads can omit these values; absence is not a zero reward or streak.
  coinsGained: z.number().nonnegative().optional(),
  experienceGained: z.number().nonnegative().optional(),
  currentStreak: z.number().int().nonnegative().optional(),
  rewardEligible: z.boolean(),
  lastWonDate: z.string().optional(),
  lastCompletedDate: z.string().optional(),
};
const frame = {
  difficulty: z.number().int().min(1).max(5).optional(),
  status,
  frameIndex: z.number().int().min(0).max(5),
  revealedFrames: z.array(z.number().int().min(0).max(5)),
  screenshots: z.array(z.string().min(1)).max(6),
  correctTitleId: z.string().optional(),
  correctTitleName: z.string().optional(),
};
const snapshotMetadata = {
  difficulty: z.number().int().min(1).max(5).optional(),
  // Archive start/restoration responses contain the selected puzzle, not the category catalogue.
  availableCategories: z.array(z.string()).optional(),
  date: z.string(),
  isArchive: z.boolean(),
  questionId: z.string().optional(),
  rewardCategory: z.string().optional(),
  rewardConsumed: z.boolean(),
};
// Before start the API returns only metadata, not a populated game/reward DTO.
const emptySnapshot = z.object({
  ...snapshotMetadata,
  status: z.literal('none'),
  category: z.string().optional(),
  frameIndex: z.literal(0),
  revealedFrames: z.array(z.number().int().min(0).max(5)).length(0),
  screenshots: z.array(z.string()).length(0).optional(),
  rewardEligible: z.boolean(),
  coinsGained: z.literal(0).optional(),
  experienceGained: z.literal(0).optional(),
  currentStreak: z.number().int().nonnegative().optional(),
});
const startedSnapshot = z.object({
  ...snapshotMetadata,
  ...reward,
  ...frame,
  status: z.enum(['playing', 'won', 'lost']),
  category: z.string(),
});
export const dailySnapshot = z.union([emptySnapshot, startedSnapshot]).transform((state) => ({
  ...state,
  category: state.category ?? '',
  screenshots: state.screenshots ?? [],
  correctTitleName: state.status === 'none' ? undefined : state.correctTitleName,
  correctTitleId: state.status === 'none' ? undefined : state.correctTitleId,
}));
export const dailyAnswer = z.object({
  ...reward,
  ...frame,
  status: z.enum(['playing', 'won', 'lost']),
  correct: z.boolean(),
  // The server includes this flag for skips and omits it for ordinary text answers.
  skipped: z.boolean().default(false),
});
export const dailyToday = z.object({ date: z.string(), assignments: z.record(z.string(), z.string()) });
export const dailyArchive = z.object({
  data: z.array(
    z.object({
      date: z.string(),
      categories: z.array(
        z
          .object({
            category: z.string(),
            accessSource: z.enum(['free', 'premium', 'purchased', 'locked']),
            accessible: z.boolean(),
            permanent: z.boolean(),
            unlockPrice: z.number().nonnegative().optional(),
          })
          // Accessible entries need no purchase price. Locked entries must provide a real server price.
          .refine((entry) => entry.accessible || entry.unlockPrice !== undefined, {
            path: ['unlockPrice'],
            message: 'Locked archive category requires an unlock price',
          })
          .transform((entry) => ({ ...entry, unlockPrice: entry.unlockPrice ?? 0 })),
      ),
    }),
  ),
});
export type DailySnapshot = z.infer<typeof dailySnapshot>;
export type DailyAnswer = z.infer<typeof dailyAnswer>;
export type DailyToday = z.infer<typeof dailyToday>;
export type DailyArchiveEntry = z.infer<typeof dailyArchive>['data'][number];

/** Only media explicitly revealed by the server can be displayed. */
export const dailyFrames = (snapshot: Pick<DailySnapshot, 'revealedFrames' | 'screenshots'>) =>
  snapshot.revealedFrames
    .map((index, position) => ({ index, url: snapshot.screenshots[position] }))
    .filter((f) => !!f.url);
